import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../authContext";
import { getTemplate, getTemplatesList, sendMessage, sendTemplateMessage } from "../../../api/login.service";
import { getInstagramMessageUsers, getInstagramUserMessages, sendInstagramHumanReply, uploadInstagramMedia } from "../../../api/instagram.service";
import {
  BusinessSetup,
  LeadState,
  emptyLead,
  applyFormFilled,
  applyStaffNotes,
  foldChats,
  leadState,
  loadSetup,
  businessId,
  primaryKey,
  saveChannelId,
  saveSetup,
} from "./businessSetup";
import { getBusinessLeads, getLeadPipeline, getWhatsAppMessages, rescoreLead } from "../../../api/leads.service";
import { ChatChannel, ChatRow, ago, channelThread, initials, leadsToChats, loadAccount, scoreTone } from "./chats";
import CreateLeadDialog from "./CreateLeadDialog";

type Bubble = { id: string; text: string; mine: boolean; at: string; mediaUrl?: string; mediaKind?: "image" | "video" };
type ComposeMode = "text" | "template";
type ApprovedTemplate = {
  id: string;
  name: string;
  language: string;
  category: string;
  content: string;
  headerImage: string;
};

function templateVariables(content: string): string[] {
  const found = content.match(/\{\{([^}]+)\}\}/g) || [];
  return Array.from(new Set(found.map((token) => token.replace(/\{\{|\}\}/g, "").trim()).filter(Boolean)));
}

function languageCode(raw: unknown): string {
  const value = typeof raw === "string" ? raw : String((raw as { code?: string } | null)?.code || "");
  if (!value) return "en_US";
  if (value.includes("_")) return value;
  return `${value}_US`;
}

export default function InboxPage() {
  const navigate = useNavigate();
  const { displayPhoneNumber } = useAuth();
  const [params] = useSearchParams();
  const [setup, setSetup] = useState<BusinessSetup>(() => loadSetup());
  const [viewChannel, setViewChannel] = useState<ChatChannel>(() => {
    const requested = params.get("channel");
    return requested === "instagram" || requested === "web" ? requested : "whatsapp";
  });
  const [accountLabel, setAccountLabel] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [instagramId, setInstagramId] = useState("");
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [selectedKey, setSelectedKey] = useState(params.get("lead") || "");
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState("");
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const [composeMode, setComposeMode] = useState<ComposeMode>("text");
  const [templates, setTemplates] = useState<ApprovedTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [templateVars, setTemplateVars] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [humanAgent, setHumanAgent] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [showList, setShowList] = useState(true);
  const [showInfo, setShowInfo] = useState(true);
  const [stageError, setStageError] = useState("");
  const [noteStatus, setNoteStatus] = useState("");
  const [scoringKey, setScoringKey] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const persist = (next: BusinessSetup) => {
    saveSetup(next);
    setSetup(next);
  };

  const keepChats = (loaded: ChatRow[]) => {
    setChats(loaded);
    setSetup((current) => {
      const next = foldChats(current, loaded);
      if (next !== current) saveSetup(next);
      return next;
    });
  };

  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const account = await loadAccount(displayPhoneNumber);
      setPhoneNumberId(account.phoneNumberId);
      setBusinessPhone(account.businessPhone);
      setInstagramId(account.instagramId);
      setAccountLabel(account.businessPhone || "this business");
      const leads = await getBusinessLeads({
        whatsappBusinessId: account.businessPhone,
        instagramBusinessId: account.instagramId,
      });
      const remote = await getLeadPipeline(businessId()).catch((): null => null);
      const loaded = leadsToChats(leads);
      setChats(loaded);
      setSetup((current) => {
        const withPipeline = remote
          ? { ...current, stages: remote.stages, tags: remote.tags, fields: remote.fields }
          : current;
        const next = applyStaffNotes(applyFormFilled(foldChats(withPipeline, loaded), loaded), loaded);
        if (next !== current) saveSetup(next);
        return next;
      });
    } catch {
      keepChats([]);
      setListError("Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, [displayPhoneNumber]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const selected = chats.find((chat) => chat.key === selectedKey) || null;
  const selectedLead = selected ? leadState(setup, selected.key) : emptyLead();
  const threadChat = selected ? channelThread(selected, viewChannel) : null;

  const threadCustomerId = selected?.customerId || "";
  const threadCustomerNumber = selected?.customerNumber || "";
  const threadInstagram = (selected?.instagramId || "").replace(/^@/, "");

  useEffect(() => {
    const chat = selected ? channelThread(selected, viewChannel) : null;
    if (!chat) {
      setBubbles([]);
      setThreadLoading(false);
      return;
    }
    let cancelled = false;
    setThreadLoading(true);
    setSendError(null);
    setBubbles([]);
    (async () => {
      try {
        if (chat.channel === "instagram") {
          const handle = (chat.instagramId || "").replace(/^@/, "");
          let customerId = chat.customerId || "";
          if (!customerId && /^\d{8,}$/.test(handle)) customerId = handle;
          if (!customerId) {
            if (!handle) {
              if (!cancelled) setBubbles([]);
              return;
            }
            const users = await getInstagramMessageUsers(instagramId || undefined);
            const match = users.find(
              (user) => (user.username || "").replace(/^@/, "").toLowerCase() === handle.toLowerCase()
            );
            if (!match?.customerId) {
              if (!cancelled) setBubbles([]);
              return;
            }
            customerId = match.customerId;
          }
          const thread = await getInstagramUserMessages(customerId, instagramId || undefined);
          if (!cancelled) setBubbles(thread.map(toThreadBubble));
          return;
        }
        if (chat.channel === "whatsapp" && chat.customerNumber) {
          const thread = await getWhatsAppMessages(chat.customerNumber, businessPhone);
          if (!cancelled) setBubbles(thread.map(toThreadBubble));
        }
      } catch {
        if (!cancelled) {
          setBubbles([]);
          setSendError("Could not load this conversation.");
        }
      } finally {
        if (!cancelled) setThreadLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedKey, viewChannel, threadCustomerId, threadCustomerNumber, threadInstagram, instagramId, phoneNumberId, businessPhone]);

  useEffect(() => {
    setMediaFile(null);
    setNoteStatus("");
  }, [selectedKey, viewChannel]);

  useEffect(() => {
    if (!mediaFile) {
      setMediaPreview("");
      return;
    }
    const url = URL.createObjectURL(mediaFile);
    setMediaPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaFile]);

  const counts = useMemo(() => ({ all: chats.length }), [chats]);

  const visible = chats.filter((chat) => {
    const lead = leadState(setup, chat.key);
    if (stageFilter && lead.stageId !== stageFilter) return false;
    const haystack = `${chat.name} ${chat.handle} ${chat.preview}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  const stageCounts = useMemo(() => {
    const countsById: Record<string, number> = {};
    const seen = new Set<string>();
    chats.forEach((chat) => {
      const id = primaryKey(setup, chat.key);
      if (seen.has(id)) return;
      seen.add(id);
      const stageId = leadState(setup, id).stageId;
      if (!stageId) return;
      countsById[stageId] = (countsById[stageId] || 0) + 1;
    });
    return countsById;
  }, [chats, setup]);

  const patchLead = (key: string, patch: Partial<LeadState>) => {
    const id = primaryKey(setup, key);
    const current = leadState(setup, id);
    persist({
      ...setup,
      leads: { ...setup.leads, [id]: { ...current, ...patch } },
    });
  };

  const lastCustomerAt = [...bubbles].reverse().find((bubble) => !bubble.mine)?.at;
  const outsideWindow = viewChannel === "whatsapp" && threadChat && lastCustomerAt
    ? Date.now() - new Date(lastCustomerAt).getTime() > 24 * 60 * 60 * 1000
    : false;
  const selectedTemplate = templates.find((item) => item.id === templateId) || null;
  const variableKeys = templateVariables(selectedTemplate?.content || "");
  const filledTemplate = (selectedTemplate?.content || "").replace(
    /\{\{\s*([^}]+)\s*\}\}/g,
    (_, key) => templateVars[String(key).trim()] || ""
  );

  useEffect(() => {
    if (outsideWindow) setComposeMode("template");
  }, [outsideWindow]);

  useEffect(() => {
    if (viewChannel !== "whatsapp" || composeMode !== "template") return;
    const wabaId = sessionStorage.getItem("wabaId") || "";
    if (!wabaId) return;
    let cancelled = false;
    setTemplatesLoading(true);
    getTemplatesList(wabaId)
      .then(async (data) => {
        const approved = ((data?.data || []) as Array<Record<string, unknown>>).filter((item) => item.status === "APPROVED");
        const listed = await Promise.all(approved.map(async (item) => {
          const id = String(item.id || item.name || "");
          let content = "";
          let language = item.language;
          let category = String(item.category || "MARKETING");
          let headerImage = "";
          try {
            const full = await getTemplate(id);
            const parts = Array.isArray(full?.components) ? full.components : [];
            const body = parts.find((part: { type?: string; text?: string }) => part.type === "BODY")?.text;
            const header = parts.find((part: { type?: string; format?: string; example?: { header_handle?: string[]; header_url?: string[] } }) => part.type === "HEADER" && part.format === "IMAGE");
            const link = header?.example?.header_handle?.[0] || header?.example?.header_url?.[0] || "";
            content = String(body || "");
            headerImage = String(link).startsWith("https://") ? String(link) : "";
            language = full?.language || language;
            category = String(full?.category || category);
          } catch {
            content = "";
          }
          return {
            id,
            name: String(item.name || "Template"),
            language: languageCode(language),
            category,
            content,
            headerImage,
          };
        }));
        if (!cancelled) setTemplates(listed.filter((item) => item.id));
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewChannel, composeMode]);

  const chooseMedia = (file: File | null) => {
    if (!file) {
      setMediaFile(null);
      return;
    }
    const video = file.type.startsWith("video/");
    const image = file.type.startsWith("image/");
    if (!video && !image) {
      setSendError("Choose an image or a video.");
      return;
    }
    const limit = video ? 25 * 1024 * 1024 : 8 * 1024 * 1024;
    if (file.size > limit) {
      setSendError(video ? "Video must be 25 MB or smaller." : "Image must be 8 MB or smaller.");
      return;
    }
    setSendError(null);
    setMediaFile(file);
  };

  const send = async () => {
    const text = draft.trim();
    if (!threadChat || sending || outsideWindow || (!text && !mediaFile)) return;
    setSending(true);
    setSendError(null);
    try {
      if (threadChat.channel === "instagram" && threadChat.customerId) {
        if (mediaFile) {
          const uploaded = await uploadInstagramMedia(mediaFile);
          if (!uploaded.success || !uploaded.url) {
            setSendError(uploaded.error || "Could not upload this file.");
            return;
          }
          const mediaKind = uploaded.kind === "VIDEO" ? "video" : "image";
          const result = await sendInstagramHumanReply({
            recipientId: threadChat.customerId,
            attachment: { type: mediaKind, url: uploaded.url },
            businessIgId: instagramId || undefined,
            humanAgent,
          });
          if (!result?.success) {
            setSendError(result?.error || "Could not send this file.");
            return;
          }
          setBubbles((current) => current.concat({
            id: `local-media-${Date.now()}`,
            text: "",
            mediaUrl: uploaded.url,
            mediaKind,
            mine: true,
            at: new Date().toISOString(),
          }));
          setMediaFile(null);
        }
        if (text) {
          const result = await sendInstagramHumanReply({
            recipientId: threadChat.customerId,
            text,
            businessIgId: instagramId || undefined,
            humanAgent,
          });
          if (!result?.success) {
            setSendError(result?.error || "Could not send this reply.");
            return;
          }
        }
      } else if (threadChat.channel === "whatsapp") {
        if (!text) return;
        await sendMessage(threadChat.customerNumber, text, phoneNumberId, businessPhone);
      }
      if (text) {
        setBubbles((current) => current.concat({
          id: `local-${Date.now()}`,
          text,
          mine: true,
          at: new Date().toISOString(),
        }));
        setDraft("");
      }
    } catch {
      setSendError("Could not send this reply.");
    } finally {
      setSending(false);
    }
  };

  const sendTemplate = async () => {
    if (!threadChat || threadChat.channel !== "whatsapp" || !selectedTemplate || sending) return;
    if (variableKeys.some((key) => !(templateVars[key] || "").trim())) {
      setSendError("Fill in every template field.");
      return;
    }
    const digits = (threadChat.customerNumber || "").replace(/\D/g, "");
    if (digits.length < 8) {
      setSendError("This lead has no WhatsApp number.");
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      const full = await getTemplate(selectedTemplate.id);
      const parts = Array.isArray(full?.components) ? full.components : [];
      const header = parts.find((part: { type?: string; format?: string; example?: { header_handle?: string[]; header_url?: string[] } }) => part.type === "HEADER" && part.format === "IMAGE");
      const headerLink = header?.example?.header_handle?.[0] || header?.example?.header_url?.[0] || selectedTemplate.headerImage || "";
      const headerImage = String(headerLink).startsWith("https://") ? String(headerLink) : "";
      if (header && !headerImage) {
        setSendError("This template needs an image, and no image link is saved on it.");
        return;
      }
      const components: unknown[] = [];
      if (headerImage) {
        components.push({
          type: "header",
          parameters: [{ type: "image", image: { link: headerImage } }],
        });
      }
      if (variableKeys.length) {
        components.push({
          type: "body",
          parameters: variableKeys.map((key) => ({ type: "text", text: templateVars[key] || "" })),
        });
      }
      await sendTemplateMessage({
        customerPhone: `+${digits}`,
        businessPhoneNumberId: phoneNumberId,
        businessPhoneNumber: businessPhone,
        template_name: selectedTemplate.name,
        language_code: selectedTemplate.language,
        category: selectedTemplate.category || "MARKETING",
        components: components.length ? components : undefined,
        message: filledTemplate || selectedTemplate.name,
      });
      setBubbles((current) => current.concat({
        id: `local-${Date.now()}`,
        text: filledTemplate || selectedTemplate.name,
        mediaUrl: headerImage || undefined,
        mediaKind: headerImage ? "image" : undefined,
        mine: true,
        at: new Date().toISOString(),
      }));
      setTemplateVars({});
    } catch {
      setSendError("Could not send this template.");
    } finally {
      setSending(false);
    }
  };

  const stageOf = (chat: ChatRow) => setup.stages.find((stage) => stage.id === leadState(setup, chat.key).stageId);

  return (
    <div className="flex h-[calc(100vh-88px)] min-h-[560px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 overflow-x-auto border-b bg-gray-50 px-3 py-2">
        <button
          type="button"
          onClick={() => navigate("/events/crm")}
          className="whitespace-nowrap rounded-full bg-white px-3 py-1 text-sm font-semibold text-blue-700 shadow-sm"
        >
          All Chats ({counts.all})
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => setShowList((open) => !open)} className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700">
            {showList ? "Hide chats" : "Show chats"}
          </button>
          <button type="button" onClick={() => setShowInfo((open) => !open)} className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700">
            {showInfo ? "Hide info" : "Show info"}
          </button>
          <button type="button" onClick={() => setCreateOpen(true)} className="whitespace-nowrap rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white">
            Create
          </button>
        </div>
      </div>

      {setup.stages.length > 0 ? (
        <div className="flex gap-1 overflow-x-auto border-b px-3 py-2">
          {setup.stages.map((stage) => {
            const active = stageFilter === stage.id;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() => setStageFilter(active ? "" : stage.id)}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-xs font-semibold text-white ${active ? "ring-2 ring-offset-1 ring-gray-900" : ""}`}
                style={{ background: stage.color }}
              >
                {stageCounts[stage.id] || 0} {stage.name}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="border-b px-3 py-2 text-sm text-gray-500">Add a stage. A message becomes a lead in the first stage.</p>
      )}

      <div className={`grid min-h-0 flex-1 grid-cols-1 ${showList && showInfo ? "lg:grid-cols-[300px_minmax(0,1fr)_280px]" : showList ? "lg:grid-cols-[300px_minmax(0,1fr)]" : showInfo ? "lg:grid-cols-[minmax(0,1fr)_280px]" : ""}`}>
        <section className={`min-h-0 flex-col border-r ${showList ? (selected ? "hidden lg:flex" : "flex") : "hidden"}`}>
          <div className="border-b px-3 py-3">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search or start a new chat"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? <p className="p-4 text-sm text-gray-500">Loading conversations...</p> : null}
            {!loading && listError ? (
              <p className="p-4 text-sm text-red-600">
                {listError}
              </p>
            ) : null}
            {!loading && !listError && visible.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No leads yet. Create one, or a person appears here when they message.</p>
            ) : null}
            {visible.filter((chat, index, list) => list.findIndex((other) => primaryKey(setup, other.key) === primaryKey(setup, chat.key)) === index).map((chat) => {
              const stage = stageOf(chat);
              const active = chat.key === selectedKey;
              return (
                <button
                  key={chat.key}
                  type="button"
                  onClick={() => { setSelectedKey(chat.key); setViewChannel(chat.channel); setStageError(""); }}
                  className={`flex w-full items-start gap-3 border-b px-3 py-3 text-left ${active ? "bg-blue-50" : "hover:bg-gray-50"}`}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700">
                    {initials(chat.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-semibold text-gray-900">{chat.name}</span>
                        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${scoreTone(chat.score)}`}>{chat.score ?? 0}</span>
                      </span>
                      <span className="shrink-0 text-[11px] text-gray-400">{ago(chat.at)}</span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-gray-500">{chat.preview || chat.handle}</span>
                    {stage ? (
                      <span className="mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium text-white" style={{ background: stage.color }}>
                        {stage.name}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className={`min-h-0 flex-col bg-[#f4f1ea] ${selected || !showList ? "flex" : "hidden lg:flex"}`}>
          {!selected ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
              <p className="text-lg font-semibold text-gray-800">No chat selected</p>
              <p className="mt-1 max-w-sm text-sm text-gray-500">Pick a conversation from the list to engage with your leads.</p>
            </div>
          ) : (
            <>
              <div className="border-b bg-white px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button type="button" className="text-sm text-blue-600 lg:hidden" onClick={() => { setSelectedKey(""); setStageError(""); }}>
                      Back
                    </button>
                    <div>
                      <p className="flex items-center gap-2 font-semibold text-gray-900">
                        {selected.name}
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${scoreTone(selected.score)}`}>{selected.score ?? 0}/100</span>
                      </p>
                      <p className="text-xs text-gray-500">{selected.handle}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setReminderOpen(true)} className="text-sm font-medium text-blue-600">
                    Reminder
                  </button>
                </div>
                <div className="mt-3 flex gap-1">
                  {(["whatsapp", "instagram", "web"] as ChatChannel[]).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setViewChannel(item)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        viewChannel === item ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {item === "web" ? "Web" : item === "whatsapp" ? "WhatsApp" : "Instagram"}
                    </button>
                  ))}
                </div>
                <p className="mt-2 truncate text-xs text-gray-500">Connected to {accountLabel || "this business"}</p>
              </div>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
                {viewChannel === "web" ? (
                  (selected.forms || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No web form for this person yet.</p>
                  ) : (
                    (selected.forms || []).map((form, index) => (
                      <div key={`${form.at}-${index}`} className="flex justify-start">
                        <div className="max-w-[75%] rounded-lg bg-white px-3 py-2 text-sm text-gray-900 shadow-sm">
                          <p className="whitespace-pre-wrap break-words">{form.text}</p>
                          <p className="mt-1 text-right text-[10px] text-gray-500">{form.at ? new Date(form.at).toLocaleString() : ""}</p>
                        </div>
                      </div>
                    ))
                  )
                ) : null}
                {viewChannel !== "web" && threadLoading ? <p className="text-sm text-gray-500">Loading conversation...</p> : null}
                {viewChannel !== "web" && !threadLoading && !threadChat ? (
                  <p className="text-sm text-gray-500">
                    {`No ${viewChannel === "whatsapp" ? "WhatsApp" : "Instagram"} conversation for this lead yet.`}
                  </p>
                ) : null}
                {viewChannel !== "web" && !threadLoading && threadChat && bubbles.length === 0 ? <p className="text-sm text-gray-500">No messages yet.</p> : null}
                {viewChannel !== "web" ? bubbles.filter((bubble) => bubble.text || bubble.mediaUrl).map((bubble) => (
                  <div key={bubble.id} className={`flex ${bubble.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${bubble.mine ? "bg-[#d9fdd3] text-gray-900" : "bg-white text-gray-900"}`}>
                      {bubble.mediaKind === "image" && bubble.mediaUrl ? (
                        <img src={bubble.mediaUrl} alt="" className="max-h-64 rounded-md" />
                      ) : null}
                      {bubble.mediaKind === "video" && bubble.mediaUrl ? (
                        <video src={bubble.mediaUrl} controls className="max-h-64 rounded-md" />
                      ) : null}
                      {bubble.text ? (
                        <p className={`whitespace-pre-wrap break-words ${bubble.mediaUrl ? "mt-2" : ""}`}>{bubble.text}</p>
                      ) : null}
                      <p className="mt-1 text-right text-[10px] text-gray-500">{bubble.at ? new Date(bubble.at).toLocaleString() : ""}</p>
                    </div>
                  </div>
                )) : null}
              </div>
              {viewChannel === "web" ? null : <div className="border-t bg-white p-3">
                {outsideWindow ? (
                  <p className="mb-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-700">
                    This conversation is outside the 24-hour window. An approved template is required before another plain message can be sent.
                  </p>
                ) : null}
                {threadChat?.channel === "instagram" ? (
                  <label className="mb-2 flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={humanAgent} onChange={(event) => setHumanAgent(event.target.checked)} />
                    Human Agent {humanAgent ? "(after 24 hours, up to 7 days)" : "(inside the 24-hour window)"}
                  </label>
                ) : null}
                {viewChannel === "whatsapp" ? (
                  <div className="mb-2 flex gap-4 text-sm text-gray-700">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="compose-mode"
                        checked={composeMode === "text"}
                        disabled={outsideWindow}
                        onChange={() => setComposeMode("text")}
                      />
                      Text
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="compose-mode"
                        checked={composeMode === "template"}
                        onChange={() => setComposeMode("template")}
                      />
                      Template
                    </label>
                  </div>
                ) : null}
                {viewChannel === "whatsapp" && composeMode === "template" ? (
                  <div className="mb-2 space-y-2">
                    <select
                      value={templateId}
                      onChange={(event) => {
                        setTemplateId(event.target.value);
                        setTemplateVars({});
                        setSendError(null);
                      }}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    >
                      <option value="">{templatesLoading ? "Loading templates..." : "Choose a template"}</option>
                      {templates.map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </select>
                    {selectedTemplate ? (
                      <>
                        {selectedTemplate.headerImage ? (
                          <img src={selectedTemplate.headerImage} alt="" className="max-h-40 rounded-md" />
                        ) : null}
                        <p className="whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-800">{filledTemplate || selectedTemplate.content || selectedTemplate.name}</p>
                        {variableKeys.map((key) => (
                          <label key={key} className="block text-xs text-gray-600">
                            {key}
                            <input
                              value={templateVars[key] || ""}
                              onChange={(event) => setTemplateVars((current) => ({ ...current, [key]: event.target.value }))}
                              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900"
                            />
                          </label>
                        ))}
                      </>
                    ) : null}
                    <button
                      type="button"
                      onClick={sendTemplate}
                      disabled={sending || !selectedTemplate || !threadChat}
                      className="rounded-2xl bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-200 disabled:text-gray-500"
                    >
                      {sending ? "Sending..." : "Send"}
                    </button>
                  </div>
                ) : (
                <div>
                  {viewChannel === "instagram" && mediaPreview ? (
                    <div className="mb-2 flex items-start gap-2">
                      {mediaFile?.type.startsWith("video/") ? (
                        <video src={mediaPreview} className="h-20 rounded-md" />
                      ) : (
                        <img src={mediaPreview} alt="" className="h-20 rounded-md" />
                      )}
                      <button type="button" onClick={() => setMediaFile(null)} className="text-xs text-gray-500">Remove</button>
                    </div>
                  ) : null}
                  <div className="flex items-end gap-2">
                    {viewChannel === "instagram" ? (
                      <>
                        <input
                          ref={mediaInputRef}
                          type="file"
                          accept="image/*,video/*"
                          className="hidden"
                          onChange={(event) => {
                            chooseMedia(event.target.files?.[0] || null);
                            event.target.value = "";
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => mediaInputRef.current?.click()}
                          disabled={!threadChat || sending}
                          className="rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 disabled:text-gray-400"
                        >
                          Image / Video
                        </button>
                      </>
                    ) : null}
                    <textarea
                      value={draft}
                      rows={2}
                      disabled={outsideWindow || !threadChat}
                      placeholder={viewChannel === "instagram" && mediaFile ? "Add words with this image or video" : "Type a message"}
                      onChange={(event) => setDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          send();
                        }
                      }}
                      className="min-h-[44px] flex-1 resize-none rounded-2xl border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={send}
                      disabled={sending || outsideWindow || (!draft.trim() && !mediaFile) || !threadChat}
                      className="rounded-2xl bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-200 disabled:text-gray-500"
                    >
                      {sending ? "Sending..." : "Send"}
                    </button>
                  </div>
                </div>
                )}
                {sendError ? <p className="mt-2 text-xs text-red-600">{sendError}</p> : null}
              </div>}
            </>
          )}
        </section>

        <aside className={`min-h-0 flex-col overflow-y-auto border-l bg-white ${showInfo && selected ? "hidden lg:flex" : "hidden"}`}>
          {selected ? (
            <div className="space-y-4 p-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400">Personal info</p>
                <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
                  {selected.name}
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${scoreTone(selected.score)}`}>{selected.score ?? 0}/100</span>
                </p>
                <p className="text-sm text-gray-600">{selected.handle}</p>
                <p className="mt-3 text-xs uppercase tracking-wide text-gray-400">Why this score</p>
                <p className="mt-1 text-sm text-gray-800">{selected.scoreReason || "-"}</p>
                <p className="mt-3 text-xs uppercase tracking-wide text-gray-400">Follow-up</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{selected.followUpScript || "-"}</p>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Same person on every channel. A WhatsApp number typed here matches a later WhatsApp chat or call.</p>
                <label className="block text-sm text-gray-700">
                  WhatsApp number
                  <input
                    key={`${selected.key}:wa:${selectedLead.whatsappNumber}`}
                    defaultValue={selectedLead.whatsappNumber}
                    onBlur={(event) => persist(saveChannelId(setup, selected.key, "whatsappNumber", event.target.value))}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  Instagram
                  <input
                    key={`${selected.key}:ig:${selectedLead.instagramId}`}
                    defaultValue={selectedLead.instagramId}
                    onBlur={(event) => persist(saveChannelId(setup, selected.key, "instagramId", event.target.value))}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  Phone
                  <input
                    key={`${selected.key}:phone:${selectedLead.phoneId}`}
                    defaultValue={selectedLead.phoneId}
                    onBlur={(event) => persist(saveChannelId(setup, selected.key, "phoneId", event.target.value))}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </label>
                {setup.fields.map((field) => (
                  <label key={field.id} className="block text-sm text-gray-700">
                    {field.name}
                    <input
                      key={`${selected.key}:field:${field.id}:${selectedLead.attributes[field.id] || ""}`}
                      defaultValue={selectedLead.attributes[field.id] || ""}
                      onBlur={(event) => patchLead(selected.key, {
                        attributes: { ...selectedLead.attributes, [field.id]: event.target.value },
                      })}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                    />
                  </label>
                ))}
                <label className="block text-sm text-gray-700">
                  Notes
                  <textarea
                    ref={noteRef}
                    key={`${selected.key}:notes:${selectedLead.notes}`}
                    defaultValue={selectedLead.notes}
                    rows={4}
                    disabled={scoringKey === selected.key}
                    placeholder="Write a note about this lead"
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm disabled:bg-gray-50"
                  />
                </label>
                  <button
                    type="button"
                    disabled={scoringKey === selected.key}
                    onClick={() => {
                      const notes = noteRef.current?.value.trim() || "";
                      const key = selected.key;
                      if (notes.length < 1 || notes.length > 2000) {
                        setNoteStatus("Write a note of 1 to 2000 characters.");
                        return;
                      }
                      const conversationId = key.startsWith("lead:") ? key.slice(5) : "";
                      if (!conversationId) {
                        setNoteStatus("This lead cannot be scored yet.");
                        return;
                      }
                      setScoringKey(key);
                      setNoteStatus("Saving and scoring…");
                      rescoreLead({
                        whatsappBusinessId: businessPhone,
                        instagramBusinessId: instagramId,
                        conversationId,
                        note: notes,
                      }).then((saved) => {
                        patchLead(key, { notes });
                        setNoteStatus("Score updated");
                        setChats((current) => current.map((chat) => (
                          chat.key === key
                            ? { ...chat, score: saved.score, scoreReason: saved.scoreReason, followUpScript: saved.followUpScript }
                            : chat
                        )));
                      }).catch(() => setNoteStatus("Could not update the score. The note was not saved."))
                        .finally(() => setScoringKey(""));
                    }}
                    className="mt-2 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white disabled:bg-gray-300"
                  >
                    {scoringKey === selected.key ? "Saving and scoring…" : "Save note & rescore"}
                  </button>
                  {noteStatus ? <span className="mt-1 block text-xs text-gray-500">{noteStatus}</span> : null}
              </div>
              <label className="flex items-center justify-between text-sm text-gray-700">
                AI auto-reply
                <input
                  type="checkbox"
                  checked={selectedLead.aiOn}
                  onChange={(event) => patchLead(selected.key, { aiOn: event.target.checked })}
                />
              </label>
              <p className="text-xs text-gray-400">For this lead. Stages below belong to this business.</p>
              <label className="block text-sm text-gray-700">
                Stage
                <select
                  value={selectedLead.stageId}
                  onChange={(event) => {
                    const nextId = event.target.value;
                    const nextStage = setup.stages.find((stage) => stage.id === nextId);
                    if (nextStage?.requireAttribute && nextStage.attributeName) {
                      const filled = (selectedLead.attributes[nextStage.attributeName] || "").trim();
                      if (!filled) {
                        setStageError(`Fill in ${nextStage.attributeName} before this lead can move to ${nextStage.name}.`);
                        return;
                      }
                    }
                    setStageError("");
                    patchLead(selected.key, { stageId: nextId });
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                >
                  {setup.stages.length === 0 ? <option value="">No stage yet</option> : null}
                  {setup.stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                </select>
              </label>
              {stageError ? <p className="text-xs text-red-600">{stageError}</p> : null}
              {setup.stages.filter((stage) => stage.requireAttribute && stage.attributeName).map((stage) => (
                <label key={stage.id} className="block text-sm text-gray-700">
                  {stage.attributeName}
                  <span className="ml-1 text-xs text-gray-400">needed for {stage.name}</span>
                  <input
                    value={selectedLead.attributes[stage.attributeName] || ""}
                    onChange={(event) => patchLead(selected.key, {
                      attributes: { ...selectedLead.attributes, [stage.attributeName]: event.target.value },
                    })}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                  />
                </label>
              ))}
              {selectedLead.lastCall ? (
                <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
                  Last call · {selectedLead.lastCall.direction}
                  {selectedLead.lastCall.at ? ` · ${new Date(selectedLead.lastCall.at).toLocaleString()}` : ""}
                  {selectedLead.lastCall.durationSeconds != null ? ` · ${selectedLead.lastCall.durationSeconds}s` : ""}
                </p>
              ) : null}
              {selectedLead.reminder ? (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Reminder {new Date(selectedLead.reminder.dueAt).toLocaleString()}
                  {selectedLead.reminder.note ? ` — ${selectedLead.reminder.note}` : ""}
                </p>
              ) : null}
              {setup.scripts.length > 0 ? (
                <div className="space-y-3 border-t pt-3">
                  {setup.scripts.map((section) => {
                    const done = section.items.filter((_, index) => selectedLead.checks.includes(`${section.id}:${index}`)).length;
                    return (
                      <div key={section.id}>
                        <p className="text-sm font-medium text-gray-800">{section.name} <span className="text-xs text-gray-400">{done}/{section.items.length}</span></p>
                        <ul className="mt-1 space-y-1">
                          {section.items.map((item, index) => {
                            const checkId = `${section.id}:${index}`;
                            const checked = selectedLead.checks.includes(checkId);
                            return (
                              <li key={checkId}>
                                <label className="flex items-center gap-2 text-sm text-gray-600">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      const checks = checked
                                        ? selectedLead.checks.filter((id) => id !== checkId)
                                        : selectedLead.checks.concat(checkId);
                                      patchLead(selected.key, { checks });
                                    }}
                                  />
                                  {item}
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </aside>
      </div>

      {createOpen ? (
        <CreateLeadDialog
          businessPhone={businessPhone}
          instagramBusinessId={instagramId}
          onClose={() => setCreateOpen(false)}
          onCreated={(conversationId) => {
            setCreateOpen(false);
            setSelectedKey(`lead:${conversationId}`);
            loadList();
          }}
        />
      ) : null}
      {reminderOpen && selected ? (
        <ReminderDialog
          initialNote={selectedLead.reminder?.note || ""}
          onClose={() => setReminderOpen(false)}
          onSave={(reminder) => {
            patchLead(selected.key, { reminder });
            setReminderOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function ReminderDialog({
  initialNote,
  onClose,
  onSave,
}: {
  initialNote: string;
  onClose: () => void;
  onSave: (reminder: { dueAt: number; time: string; note: string }) => void;
}) {
  const [days, setDays] = useState(2);
  const [time, setTime] = useState("15:00");
  const [note, setNote] = useState(initialNote);

  const save = () => {
    const due = new Date();
    due.setDate(due.getDate() + days);
    const [hours, minutes] = time.split(":").map((part) => Number(part));
    due.setHours(hours || 0, minutes || 0, 0, 0);
    onSave({ dueAt: due.getTime(), time, note: note.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Next Reminder</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-xl text-gray-400">×</button>
        </div>
        <label className="block text-sm text-gray-600">
          Date
          <div className="mt-1 flex items-center gap-2">
            <input value={`In ${days} day${days === 1 ? "" : "s"}`} readOnly className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
        </label>
        <div className="mt-2 flex gap-2">
          {[1, 2, 3, 4].map((offset) => (
            <button
              key={offset}
              type="button"
              onClick={() => setDays(offset)}
              className={`rounded-md border px-3 py-1 text-sm ${days === offset ? "border-blue-600 text-blue-600" : "border-gray-200 text-gray-600"}`}
            >
              +{offset}
            </button>
          ))}
        </div>
        <label className="mt-3 block text-sm text-gray-600">
          Time
          <input type="time" value={time} onChange={(event) => setTime(event.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>
        <label className="mt-3 block text-sm text-gray-600">
          Reminder Note
          <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-600">Cancel</button>
          <button type="button" onClick={save} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white">Save</button>
        </div>
      </div>
    </div>
  );
}

function toThreadBubble(message: {
  mid?: string;
  text?: string;
  type?: string;
  direction?: string;
  timestamp?: string;
  updatedAt?: string;
}): Bubble {
  const lines = (message.text || "").split("\n").map((line) => line.trim()).filter(Boolean);
  const urlLine = lines.find((line) => /^https:\/\//.test(line) && (
    message.type === "image"
    || message.type === "video"
    || message.type === "attachment"
    || /\.(jpe?g|png|gif|webp)(\?|$)/i.test(line)
    || line.includes("/user-media/image/")
  ));
  const mediaKind = urlLine
    ? (message.type === "video" ? "video" as const : "image" as const)
    : undefined;
  const text = lines.filter((line) => line !== urlLine).join("\n");
  return {
    id: message.mid || `${message.timestamp}-${message.text}`,
    text: mediaKind ? text : (text || (message.type === "attachment" ? "" : message.type || "Message")),
    mediaUrl: urlLine,
    mediaKind,
    mine: message.direction === "outbound",
    at: message.updatedAt || message.timestamp,
  };
}

